/**
 * Fog of War State Management
 * Tracks which cells have been explored (ever visible) vs currently visible vs hidden.
 */

import type { FogState, VisibilityResult } from './types';

/** Create an initial fog grid — all hidden. */
export function createFogGrid(width: number, height: number): FogState[][] {
  return Array.from({ length: height }, () =>
    Array.from({ length: width }, () => 'hidden' as FogState)
  );
}

/** Create an initial light grid — all zero. */
export function createLightGrid(width: number, height: number): number[][] {
  return Array.from({ length: height }, () =>
    Array.from({ length: width }, () => 0)
  );
}

/**
 * Update fog and light grids based on a new visibility result.
 * - All cells become 'explored' at minimum (previously visible → explored).
 * - Cells in visibleSet become 'visible'.
 * - Light values update from the visibility result.
 *
 * Mutates fogGrid and lightGrid in place for performance.
 */
export function updateFog(
  fogGrid: FogState[][],
  lightGrid: number[][],
  visibility: VisibilityResult,
): void {
  const height = fogGrid.length;
  const width = fogGrid[0]?.length ?? 0;

  // First pass: demote all 'visible' to 'explored', zero out light
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (fogGrid[y][x] === 'visible') {
        fogGrid[y][x] = 'explored';
      }
      lightGrid[y][x] = 0;
    }
  }

  // Second pass: mark newly visible cells
  for (const key of visibility.visibleSet) {
    const [xStr, yStr] = key.split(',');
    const x = Number(xStr);
    const y = Number(yStr);
    if (x >= 0 && x < width && y >= 0 && y < height) {
      fogGrid[y][x] = 'visible';
      lightGrid[y][x] = visibility.lightValues.get(key) ?? 0;
    }
  }
}

/** Reset all fog to hidden. */
export function resetFog(fogGrid: FogState[][]): void {
  for (let y = 0; y < fogGrid.length; y++) {
    for (let x = 0; x < (fogGrid[0]?.length ?? 0); x++) {
      fogGrid[y][x] = 'hidden';
    }
  }
}

/** Reveal entire map (GM mode / debug). */
export function revealAll(fogGrid: FogState[][]): void {
  for (let y = 0; y < fogGrid.length; y++) {
    for (let x = 0; x < (fogGrid[0]?.length ?? 0); x++) {
      fogGrid[y][x] = 'visible';
    }
  }
}
